import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '@store';
import { getResponseErrors, ResponseError } from '@ui/src';
import { PermissionEnum } from '../user-roles/types';
import PermissionColumns from './permission-columns';
import {
  useLazyGetUserPermissionsQuery,
  useLazyGetUsersQuery,
} from './store/services/users';
import UsersColumn from './users-column';
import NotificationSettingsColumn from './settings-column';

import cn from './styles.module.css';

const Columns = ({
  activeTab,
}: {
  activeTab: 'accessRights' | 'notificationSettings';
}): React.JSX.Element => {
  const {
    userList: {
      users: { userId, users },
    },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [error, setError] = useState<string>('');
  const [errorList, setErrorList] = useState('');

  const [getUsers, { isLoading }] = useLazyGetUsersQuery();
  const [getPermissions, { isLoading: permissionsIsLoading }] =
    useLazyGetUserPermissionsQuery();

  const getUsersAction = useCallback(async () => {
    const response = (await getUsers('')) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  const getPermissionsAction = useCallback(async () => {
    if (userId) {
      const response = (await getPermissions(userId)) as {
        error: { data: { message: string } };
      };
      setError('');

      if (response.error) {
        setError(response.error.data.message);
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!users) {
      getUsersAction();
    }
  }, [users]);

  useEffect(() => {
    if (activePermissions?.includes(PermissionEnum.USERS_EDIT_ACTION)) {
      getPermissionsAction();
    }
  }, [getPermissionsAction, activePermissions]);

  return (
    <>
      {!errorList && (
        <>
          <UsersColumn isLoading={isLoading} />
          {activeTab === 'accessRights' && (
            <PermissionColumns isLoading={permissionsIsLoading} error={error} />
          )}
          {activeTab === 'notificationSettings' && (
            <NotificationSettingsColumn />
          )}
        </>
      )}
      {errorList && (
        <div className={cn.loadingError}>
          <div className={cn.loadingErrorTitle}>Unable to load data</div>
          <div className={cn.loadingErrorText}>{errorList}</div>
        </div>
      )}
    </>
  );
};

export default Columns;
